import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Τι είναι το fine line tattoo;", acceptedAnswer: { "@type": "Answer", text: "Το fine line tattoo χρησιμοποιεί εξαιρετικά λεπτές γραμμές για να δημιουργήσει λεπτομερή σχέδια με minimal εμφάνιση. Απαιτεί υψηλή τεχνική ακρίβεια." } },
    { "@type": "Question", name: "Πόσο καλά γερνά ένα fine line tattoo;", acceptedAnswer: { "@type": "Answer", text: "Ένα καλά εκτελεσμένο fine line tattoo από έμπειρο artist σε κατάλληλο placement μπορεί να γεράσει πολύ καλά. Το placement και η τεχνική είναι καθοριστικά." } },
    { "@type": "Question", name: "Ποια placements λειτουργούν καλύτερα για fine line;", acceptedAnswer: { "@type": "Answer", text: "Τα πιο σταθερά placements για fine line είναι τα forearms, upper arms και το στήθος/rib cage. Περιοχές με έντονη τριβή ή έκθεση στον ήλιο αντιμετωπίζουν πιο γρήγορο fading." } },
  ],
};

export default function FineLineTattooGuideAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Fine Line Tattoos στην Αθήνα: τι να προσέξεις πριν κλείσεις",
      "description": "Πλήρης οδηγός για fine line tattoos στην Αθήνα: τι κάνει ένα fine line quality, πώς γερνά, κοινά λάθη, placements και τι να ρωτήσεις τον artist.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/fine-line-tattoo-guide-athens",
      "inLanguage": "el"
    };
    inject('schema-fine-line-faq', faqSchema);
    inject('schema-fine-line-article', articleSchema);
    return () => {
      document.getElementById('schema-fine-line-faq')?.remove();
      document.getElementById('schema-fine-line-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Fine Line Tattoos στην Αθήνα: Οδηγός πριν κλείσεις | 210 Tattoo Athens</title>
        <meta name="description" content="Πλήρης οδηγός για fine line tattoos στην Αθήνα: τι κάνει ένα fine line quality, πώς γερνά, κοινά λάθη, placements και τι να ρωτήσεις τον artist." />
        <link rel="canonical" href="https://210tattoo.gr/blog/fine-line-tattoo-guide-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/fine-line-tattoo-guide-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/fine-line-tattoo-guide-athens" />
        <meta property="og:title" content="Fine Line Tattoos στην Αθήνα: Οδηγός πριν κλείσεις | 210 Tattoo Athens" />
        <meta property="og:description" content="Τι κάνει ένα fine line tattoo quality, πώς γερνά και τι να ρωτήσεις τον artist πριν κλείσεις ραντεβού στην Αθήνα." />
        <meta property="og:url" content="https://210tattoo.gr/blog/fine-line-tattoo-guide-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('fine-line-tattoo-guide-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fine Line Tattoos στην Αθήνα | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Οδηγός για fine line tattoos: ποιότητα, aging, placements και τι να ρωτήσεις τον artist." />
        <meta name="twitter:image" content={ogImage('fine-line-tattoo-guide-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Fine Line Tattoo Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Οδηγός για</span>
              <br />
              <span className="text-white">Fine Line tattoos</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το fine line tattoo είναι από τα πιο δημοφιλή styles τα τελευταία χρόνια, και από τα πιο απαιτητικά τεχνικά. Πριν κλείσεις ραντεβού, υπάρχουν πράγματα που αξίζει να ξέρεις.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What makes fine line quality */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι κάνει ένα fine line tattoo <span className="text-pink-500">ποιοτικό</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το fine line φαίνεται απλό από έξω. Δεν είναι. Η λεπτή γραμμή δεν συγχωρεί λάθη, αν κάτι δεν είναι σωστό, φαίνεται αμέσως. Αυτά είναι τα σημεία που ξεχωρίζουν μια καλή εκτέλεση:
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Καθαρότητα γραμμών', desc: 'Κάθε γραμμή πρέπει να ξεκινά και να τελειώνει με ακρίβεια. Τρέμουλο, ανομοιομορφία ή διακοπές είναι εμφανή σε fine line περισσότερο από οποιοδήποτε άλλο στυλ.' },
                { title: 'Σωστή πίεση βελόνας', desc: 'Πολύ βαθιά και το μελάνι «φεύγει» εκτός (blowout). Πολύ ρηχά και η γραμμή δεν κρατά. Η ισορροπία απαιτεί εμπειρία.' },
                { title: 'Αναλογίες και σύνθεση', desc: 'Σε fine line, το negative space παίζει εξίσου σημαντικό ρόλο με τις γραμμές. Η σύνθεση πρέπει να λειτουργεί στο συγκεκριμένο placement.' },
                { title: 'Κατάλληλη βελόνα για το στυλ', desc: 'Δεν κάνουν όλες οι βελόνες για fine line. Ο artist πρέπει να χρησιμοποιεί τον σωστό τύπο για το αποτέλεσμα που θέλεις.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it ages */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς γερνά <span className="text-pink-500">το fine line tattoo</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αυτή είναι η ερώτηση που πρέπει να κάνεις και που πολλοί παραλείπουν. Το aging ενός fine line εξαρτάται από τρεις κυρίως παράγοντες:
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { title: 'Τεχνική εκτέλεση', desc: 'Ένα σωστά εκτελεσμένο fine line γερνά ομοιόμορφα. Ένα με blowouts ή ανομοιόμορφη γραμμή χαλά γρηγορότερα.' },
                { title: 'Placement', desc: 'Περιοχές με τριβή (δάχτυλα, παλάμες, εσωτερικό αγκώνα) fάρουν πιο γρήγορα. Προτιμώνται πιο σταθερές περιοχές.' },
                { title: 'Φροντίδα μετά', desc: 'Ήλιος χωρίς αντηλιακό είναι ο μεγαλύτερος εχθρός του fine line. Αντηλιακό στο τατουάζ μετά την επούλωση παρατείνει σημαντικά τη ζωή του.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ένα fine line που έγινε σωστά σε κατάλληλο placement μπορεί να γεράσει πολύ καλά. Η ρεαλιστική προσδοκία είναι ότι με τον καιρό κάποιες γραμμές μπορεί να γίνουν ελαφρώς πιο diffused, αλλά αυτό δεν σημαίνει χαλασμένο αποτέλεσμα.
            </p>
          </div>
        </section>

        {/* Common mistakes */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Κοινά λάθη που <span className="text-pink-500">πρέπει να αποφύγεις</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Τα λάθη στο fine line γίνονται συνήθως πριν τη συνεδρία, στη διαδικασία επιλογής artist ή στη λήψη απόφασης για placement.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Να επιλέγεις artist που δεν εξειδικεύεται σε fine line', desc: 'Δεν κάνει κάθε artist fine line καλά. Χρειάζεσαι portfolio με αποδεδειγμένα fine line projects, ιδανικά healed.' },
                { title: 'Placement σε δύσκολες περιοχές χωρίς ενημέρωση', desc: 'Δάχτυλα, παλάμες, εσωτερικός καρπός, έχουν γρηγορότερο fading. Αν αυτό το placement είναι must για σένα, ο artist πρέπει να σου το εξηγήσει ειλικρινά.' },
                { title: 'Υπερβολικά μικρό σχέδιο με πολλή λεπτομέρεια', desc: 'Πολύ λεπτά elements σε πολύ μικρή κλίμακα δύσκολα διατηρούνται. Ο artist πρέπει να σε κατευθύνει για ρεαλιστικές αναλογίες.' },
                { title: 'Να μην ρωτήσεις για το healed αποτέλεσμα', desc: 'Κάθε fine line φαίνεται τέλειο fresh. Το αποτέλεσμα μετά από 3-6 μήνες είναι αυτό που μετράει.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Placements */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Placements που <span className="text-pink-500">λειτουργούν καλά</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Κάποια placements λειτουργούν πολύ καλύτερα μακροπρόθεσμα για fine line.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Forearm και upper arm', desc: 'Κλασικές επιλογές που γερνούν καλά. Αρκετά επίπεδες επιφάνειες, σχετικά σταθερό δέρμα.' },
                { title: 'Στήθος και rib cage', desc: 'Αγαπημένα placements για fine line με λεπτομέρεια. Λιγότερη τριβή, καλή ορατότητα.' },
                { title: 'Back και shoulder blade', desc: 'Ιδανικά για μεγαλύτερα fine line compositions. Σταθερό placement με προστατευμένο δέρμα.' },
                { title: 'Inner wrist', desc: 'Δημοφιλές placement που λειτουργεί, αλλά χρειάζεται πιο προσεκτική φροντίδα μακροπρόθεσμα λόγω έκθεσης στον ήλιο.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Questions to ask */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ερωτήσεις που πρέπει να κάνεις <span className="text-pink-500">στο consultation</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αν πηγαίνεις για fine line consultation, αυτές είναι οι ερωτήσεις που θα σου δώσουν ρεαλιστική εικόνα:
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Έχεις healed fine line δουλειές να μου δείξεις;', desc: 'Η πιο σημαντική ερώτηση. Αν δεν έχει ή αποφεύγει να δείξει, αυτό λέει πολλά.' },
                { step: '02', title: 'Πώς πιστεύεις ότι θα γεράσει αυτό το placement;', desc: 'Ένας καλός artist θα σου δώσει ειλικρινή εκτίμηση, και ίσως προτείνει εναλλακτικό placement αν υπάρχει κίνδυνος γρήγορου fading.' },
                { step: '03', title: 'Τι αλλαγές θα πρότεινες στο σχέδιο;', desc: 'Αν ο artist κοιτά το concept και δεν κάνει καμία παρατήρηση, ίσως δεν σκέφτεται βαθύτερα το project.' },
                { step: '04', title: 'Πόση λεπτομέρεια είναι ρεαλιστική στο μέγεθος που θέλω;', desc: 'Μικρό μέγεθος με πολλή λεπτομέρεια μπορεί να φαίνεται καλό τώρα αλλά να γίνει unreadable με τον καιρό.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Fine line στο <span className="text-pink-500">210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο studio μας στο Γκύζη, το fine line είναι ένα από τα κύρια styles στα οποία εξειδικευόμαστε. Κάθε fine line project ξεκινά με consultation όπου αξιολογούμε ρεαλιστικά το concept, το placement και τι είναι εφικτό μακροπρόθεσμα.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Custom σχέδιο για κάθε project', desc: 'Δεν κάνουμε copy-paste σχέδια. Κάθε fine line είναι φτιαγμένο βάσει της ιδέας σου.' },
                { title: 'Ειλικρινές feedback για placement', desc: 'Αν πιστεύουμε ότι ένα placement δεν θα λειτουργήσει καλά μακροπρόθεσμα, θα σου το πούμε, και θα προτείνουμε εναλλακτική.' },
                { title: 'Τεχνική εξειδίκευση', desc: 'Δουλεύουμε με κατάλληλα εργαλεία για fine line και έχουμε portfolio με healed δουλειές.' },
                { title: 'Aftercare guidance', desc: 'Μετά τη συνεδρία σου δίνουμε οδηγίες φροντίδας που επηρεάζουν άμεσα το πώς θα γεράσει το τατουάζ.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When fine line isn't right */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε το fine line <span className="text-pink-500">ίσως δεν είναι η καλύτερη επιλογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αυτό είναι κάτι που λίγοι artist λένε ειλικρινά: δεν ταιριάζει το fine line σε κάθε ιδέα ή κάθε τύπο δέρματος.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Πολύ σκούρος τόνος δέρματος', desc: 'Το fine line σε σκούρα δέρματα μπορεί να μην έχει την ίδια ορατότητα. Υπάρχουν εναλλακτικά styles που λειτουργούν καλύτερα.' },
                { title: 'Placements με έντονη τριβή', desc: 'Δάχτυλα, παλάμες, πέλματα, το fine line σε αυτές τις περιοχές fάρει γρήγορα και χρειάζεται συχνό touch-up.' },
                { title: 'Ιδέες που απαιτούν bold statement', desc: 'Αν θέλεις κάτι που να φαίνεται από απόσταση ή να έχει έντονη παρουσία, ίσως το blackwork ή το neo-traditional εξυπηρετεί καλύτερα.' },
                { title: 'Δέρμα που τείνει να keloid', desc: 'Αν έχεις ιστορικό με keloid scars, αυτό πρέπει να συζητηθεί πριν οποιοδήποτε tattoo, fine line ή όχι.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το consultation είναι η στιγμή που αυτά τα θέματα βγαίνουν στην επιφάνεια. Ένας artist που δεν ρωτά και απλώς δέχεται κάθε project χωρίς ανάλυση δεν σκέφτεται μακροπρόθεσμα για εσένα.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι fine line <span className="text-pink-500">tattoo στην Αθήνα;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας DM στο Instagram του 210 Tattoo Athens με την ιδέα σου. Θα αξιολογήσουμε το concept, θα σου πούμε τι είναι εφικτό και θα κλείσουμε consultation.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM για Fine Line
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να διαλέξεις tattoo artist στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέξεις πριν επιλέξεις studio: portfolio, hygiene, consultation και red flags.</p>
              </Link>
              <Link to="/blog/fine-line-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Fine Line Τατουάζ: Οδηγός πριν κλείσεις ραντεβού</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ποιότητα, γήρανση, placements και τι να αποφύγεις στα fine line τατουάζ.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
